import type { BannerRun, OwnerBannerOption } from '@clarity/contract';

/**
 * Pure calendar-day helpers for the banner-options surfaces
 * (clarity-banner-options, design §4). Run and ask-by dates are `YYYY-MM-DD`
 * calendar days with inclusive ends; every computation is pinned to UTC so the
 * figures match the server's `todayOf(deps.now())` convention.
 */

const DAY_MS = 86_400_000;

const dayMs = (day: string): number => new Date(`${day}T00:00:00.000Z`).getTime();

/** Today's UTC calendar day (`YYYY-MM-DD`). Only top-level callers should read the clock. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Length of an inclusive `[from, to]` run in days (a one-day run is 1). */
export function runLengthDays(run: Pick<BannerRun, 'from' | 'to'>): number {
  return Math.round((dayMs(run.to) - dayMs(run.from)) / DAY_MS) + 1;
}

/** Whole days from `today` until `day` (negative once `day` has passed). */
export function daysUntil(today: string, day: string): number {
  return Math.round((dayMs(day) - dayMs(today)) / DAY_MS);
}

/** A `YYYY-MM-DD` day as a short day-month-year label (UTC; unparseable ⇒ the raw string). */
export function dayMonthYear(day: string, locale?: string): string {
  const d = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** The date tile's two parts for a run start: short month and day-of-month (UTC). */
export function dateTileParts(day: string, locale?: string): { month: string; day: string } {
  const d = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return { month: '', day };
  return {
    month: d.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' }),
    day: d.toLocaleDateString(locale, { day: 'numeric', timeZone: 'UTC' }),
  };
}

/**
 * The fee head (mock `optionRow`): min/max over the open runs, labelled
 * "per run" when the option has more than one run and "for the run" when it has
 * exactly one.
 */
export function feeSummary(option: Pick<OwnerBannerOption, 'runs' | 'openRuns'>): {
  min: number;
  max: number;
  perRun: boolean;
} {
  const fees = (option.openRuns.length > 0 ? option.openRuns : option.runs).map((r) => r.fee);
  return {
    min: Math.min(...fees),
    max: Math.max(...fees),
    perRun: option.openRuns.length > 1 || option.runs.length > 1,
  };
}

/** True when two runs denote the same dated period. */
export function sameRun(a: Pick<BannerRun, 'from' | 'to'>, b: Pick<BannerRun, 'from' | 'to'> | null): boolean {
  return b !== null && a.from === b.from && a.to === b.to;
}
